import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';

export type SearchableSelectOption = {
  value: string;
  label: string;
  searchText?: string;
};

type SearchableSelectProps = {
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  searchTerm?: string;
  onSearchTermChange?: (value: string) => void;
  loading?: boolean;
  emptyMessage?: string;
  sentinelOption?: SearchableSelectOption;
  disabled?: boolean;
  triggerId?: string;
  triggerClassName?: string;
  contentClassName?: string;
  renderOption?: (option: SearchableSelectOption) => ReactNode;
  inputTestId?: string;
  maxVisibleOptions?: number;
};

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  searchTerm,
  onSearchTermChange,
  loading = false,
  emptyMessage = 'No options available',
  sentinelOption,
  disabled = false,
  triggerId,
  triggerClassName,
  contentClassName,
  renderOption,
  inputTestId,
  maxVisibleOptions,
}) => {
  const [open, setOpen] = useState(false);
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const effectiveSearchTerm = searchTerm ?? internalSearchTerm;

  useEffect(() => {
    if (!open || !searchInputRef.current) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const handleSearchTermChange = (nextValue: string) => {
    if (onSearchTermChange) {
      onSearchTermChange(nextValue);
      return;
    }
    setInternalSearchTerm(nextValue);
  };

  const filteredOptions = useMemo(() => {
    const query = effectiveSearchTerm.trim().toLowerCase();
    if (!query) {
      return options;
    }

    return options.filter((option) => {
      const haystack = `${option.label} ${option.searchText ?? ''}`.trim().toLowerCase();
      return haystack.includes(query);
    });
  }, [effectiveSearchTerm, options]);

  const displayedOptions = useMemo(() => {
    if (!maxVisibleOptions || filteredOptions.length <= maxVisibleOptions) {
      return filteredOptions;
    }

    const truncated = filteredOptions.slice(0, maxVisibleOptions);
    const selectedOption = filteredOptions.find((option) => option.value === value);

    if (!selectedOption || truncated.some((option) => option.value === selectedOption.value)) {
      return truncated;
    }

    return [selectedOption, ...truncated.slice(0, Math.max(0, maxVisibleOptions - 1))];
  }, [filteredOptions, maxVisibleOptions, value]);

  return (
    <Select value={value} onValueChange={onValueChange} onOpenChange={setOpen} disabled={disabled}>
      <SelectTrigger id={triggerId} className={triggerClassName}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={contentClassName}>
        <div className="p-2">
          <Input
            ref={searchInputRef}
            value={effectiveSearchTerm}
            onChange={(event) => handleSearchTermChange(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            placeholder={searchPlaceholder}
            data-testid={inputTestId}
          />
        </div>
        {sentinelOption ? (
          <SelectItem value={sentinelOption.value}>
            {renderOption ? renderOption(sentinelOption) : sentinelOption.label}
          </SelectItem>
        ) : null}
        {loading ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">Loading...</div>
        ) : filteredOptions.length === 0 ? (
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
        ) : (
          displayedOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {renderOption ? renderOption(option) : option.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};

export default SearchableSelect;
